import React, {useEffect} from "react"
// MUI
import {Box, CircularProgress, Typography} from "@mui/material"
import {CheckCircleRounded, RadioButtonUncheckedRounded} from "@mui/icons-material"
// Translation
import {useTranslation} from "react-i18next"
// Data
import {useUser} from "@/context/UserProvider"
import {usePermissions} from "@/context/PermissionsProvider"
import {useModule} from "@/context/ModuleProvider"
import {useData} from "@/context/PackagingDataProvider"
// Misc
import {SectionCard} from "@/components/Layout"
import {packagingTokens as tk} from "@/theme/packagingTokens"
import {stepsForRole, PackagingStep} from "@/constants/packagingSteps"
import {isMainOrgManager} from "@/utils/orgAccess"

// Dashboard "getting started" game — circular progress + the role-specific
// first steps. Completed steps strike through; clicking a step deep-links to
// the right section and spotlights the action (see StepSpotlight in Main).
export default function GettingStarted(props: {onGuideStep: (step: PackagingStep) => void}) {

    const {onGuideStep} = props
    const {t} = useTranslation(["common"])
    const {packagingStepsDone, markStepDone, token, user, fullOrganisation} = useUser()
    const {isAccountRole} = usePermissions()
    const {modules} = useModule()
    const {brands, ranges, products, getHealthchecksStatus} = useData()

    const steps = stepsForRole(isAccountRole, modules, isMainOrgManager(user, fullOrganisation))

    // Auto-complete the asset-creation steps when matching assets already
    // exist — accounts that created brands/ranges/products before the tracker
    // shipped (or outside this dashboard) shouldn't be asked to redo them.
    // markStepDone is idempotent and persists, so this only fires once per step.
    useEffect(() => {
        const assetSteps: Record<string, boolean> = {
            create_brand: Array.isArray(brands) && brands.length > 0,
            create_range: Array.isArray(ranges) && ranges.length > 0,
            create_product: Array.isArray(products) && products.length > 0,
        }
        steps.forEach((s) => { if (assetSteps[s.id]) markStepDone(s.id) })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [brands, ranges, products])

    // Same idea for healthchecks: active settings mean the step is effectively
    // done. Status isn't loaded by the data provider, so fetch it here — but
    // only while the step is pending for this role (one call, then never again).
    useEffect(() => {
        if (!token) return
        if (!steps.some((s) => s.id === "launch_healthchecks")) return
        if (packagingStepsDone?.launch_healthchecks) return
        getHealthchecksStatus().then((res: any) => {
            if (!!res && res !== "no healthchecks" && !!res.enabled) markStepDone("launch_healthchecks")
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, steps.length])

    if (!steps.length) return null

    const doneCount = steps.filter((s) => packagingStepsDone?.[s.id]).length
    const progress = Math.round((doneCount / steps.length) * 100)

    return (
        <SectionCard title={t("getting_started_title")} sx={{mt: 2.5, mb: 2.5}}>
            <Box sx={{display: "flex", gap: 4, alignItems: "center", flexWrap: "wrap"}}>
                {/* Circular progress — big & thick so the card reads as a game */}
                <Box sx={{position: "relative", display: "inline-flex", flexShrink: 0}}>
                    <CircularProgress variant={"determinate"} value={100} size={128} thickness={5.5} sx={{color: tk.color.borderLight}} />
                    <CircularProgress
                        variant={"determinate"} value={progress} size={128} thickness={5.5}
                        sx={{position: "absolute", left: 0, color: tk.color.green, "& .MuiCircularProgress-circle": {strokeLinecap: "round"}}}
                    />
                    <Box sx={{position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center"}}>
                        <Typography sx={{fontSize: "22px", fontWeight: 700}}>{doneCount}/{steps.length}</Typography>
                    </Box>
                </Box>

                {/* Steps — vertical list (the remaining width is reserved for
                    upcoming content) */}
                <Box sx={{flex: 1, minWidth: 240}}>
                    {steps.map((step) => {
                        const done = !!packagingStepsDone?.[step.id]
                        return (
                            <Box
                                key={step.id}
                                data-testid={`step-${step.id}`}
                                onClick={done ? undefined : () => onGuideStep(step)}
                                sx={{
                                    display: "flex", alignItems: "center", gap: 1, py: 0.5,
                                    cursor: done ? "default" : "pointer", borderRadius: tk.radius.sm, px: 0.75,
                                    "&:hover": done ? {} : {backgroundColor: tk.color.bg},
                                }}
                            >
                                {done
                                    ? <CheckCircleRounded sx={{fontSize: 17, color: tk.color.green}} />
                                    : <RadioButtonUncheckedRounded sx={{fontSize: 17, color: tk.color.textTertiary}} />}
                                <Typography
                                    sx={{
                                        fontSize: "13.5px",
                                        color: done ? tk.color.textTertiary : tk.color.textPrimary,
                                        textDecoration: done ? "line-through" : "none",
                                    }}
                                >
                                    {t(`step_${step.id}`)}
                                </Typography>
                            </Box>
                        )
                    })}
                </Box>
            </Box>
        </SectionCard>
    )
}
